import { DestroyRef, Directive, inject, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RealEstateContact, RealEstateService, ViewQuery } from '@eadsysteme/ewsrealestateclient-v3';
import { SessionService } from '../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';

@Directive()
export abstract class BaseSelectByRealEstateDialogComponent<T> implements OnInit {
	protected sessionService = inject(SessionService);
	protected destroyRef = inject(DestroyRef);
	protected realEstateService = inject(RealEstateService);
	protected router = inject(Router);

	protected readonly realEstates = signal<RealEstateContact[]>([]);
	protected readonly items = signal<T[]>(undefined);
	protected realEstateFc = new FormControl<RealEstateContact>(null);
	protected itemFc = new FormControl<T>(null);
	protected finishedLoading = signal(true);

	protected abstract dialogRef: MatDialogRef<any>;

	ngOnInit(): void {
		const viewQuery: ViewQuery = {};
		this.finishedLoading.set(false);
		this.realEstateService
			.search(this.sessionService.effectiveArea, viewQuery)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.finishedLoading.set(true))
			)
			.subscribe({
				next: (value) => {
					this.realEstates.set(value.items as RealEstateContact[]);
					this.realEstates().sort((a, b) => a.realEstateNumber - b.realEstateNumber);
				},
			});
	}

	protected loadItems(initialLoad: boolean = false): void {
		const realEstateId: string = this.realEstateFc.value.realEstateId;
		this.items.set(undefined);
		this.finishedLoading.set(false);
		this.fetchItems(realEstateId)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.finishedLoading.set(true))
			)
			.subscribe({
				next: (items: T[]) => {
					this.itemFc.setValue(null);
					this.items.set(items);
					if (items.length === 1) {
						this.itemFc.setValue(items[0]);
						if (initialLoad) {
							this.close(true);
						}
					}
				},
				error: () => {
					this.items.set([]);
					this.itemFc.setValue(null);
				},
			});
	}

	protected abstract fetchItems(realEstateId: string): any;
	protected abstract close(saved: boolean): void;
}
